"use client";

import Image from "next/image";
import React, { FC, Fragment, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import { cn } from "@/lib/utils";
import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { useIsMobile } from "@/hooks/useIsMobile";

/** Nullable DB strings arrive as `null`, not `undefined` — coalesce to a default. */
const str = (def = "") =>
  z
    .string()
    .nullish()
    .transform((v) => v ?? def);

/**
 * A single comparison row (category chip + CU column + traditional column),
 * stored in the `metadata` JSON array.
 */
const RowSchema = z.object({
  id: z.union([z.number(), z.string()]).nullish(),
  meta_criteria_name: str(),
  meta_criteria_image: str(),
  meta_criteria_background_color: str("#FFFFFF"),
  meta_criteria_border_color: str("#E6E6E6"),
  meta_column1_text: str(),
  meta_column1_image: str(),
  meta_column2_text: str(),
  meta_column2_image: str(),
  meta_priority: z.coerce.number().nullish().transform((v) => v ?? 0),
  /** MySQL/API may send 0/1; treat truthy as active. */
  meta_status: z
    .preprocess((v) => !(v === false || v === 0 || v === "0"), z.boolean())
    .optional()
    .default(true),
});

const Comparison1004DataSchema = z.object({
  desktop_background_color: str("#FFFFFF"),
  mobile_background_color: str("#FFFFFF"),
  pre_title: str(),
  pre_title_color: str("#E1473F"),
  title: str(),
  title_start_color: str("#000000"),
  description: str(),
  description_text_color: str("#000000"),
  column1: str("CU Online"),
  column1_text_color: str("#FFFFFF"),
  column1_background_color: str("#E1473F"),
  column2: str("Traditional Learning"),
  column2_text_color: str("#000000"),
  column2_background_color: str("#FFFFFF"),
  status: z
    .union([z.boolean(), z.number()])
    .nullish()
    .transform((v) => v ?? true),
  metadata: z.array(RowSchema).nullish().transform((v) => v ?? []),
});

type ComparisonRow = z.infer<typeof RowSchema>;

/**
 * Comparison 1004 — "CU Online vs Traditional Learning" grid, ported from the
 * Chandigarh portal's Comparison organism. CU tokens are hardcoded (heading-2/3
 * = 32/28px, primary = #E1473F, gray-350 = #E6E6E6, `w-screen` = 1114px) and
 * CU's default `lg` breakpoint is translated to `min-[1024px]:` because this
 * webapp uses a custom breakpoint scale.
 */
export const Comparison1004: FC<any> = (props) => {
  const actualData = props?.data?.data || props?.data || props;
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (sectionRef.current) {
      FireWidgetViewedEvent(sectionRef, "comparison");
    }
  }, []);

  const parsedResult = useMemo(
    () => Comparison1004DataSchema.safeParse(actualData),
    [actualData],
  );

  const parsedData = parsedResult.success ? parsedResult.data : null;

  const rows = useMemo<ComparisonRow[]>(
    () =>
      parsedData
        ? [...parsedData.metadata]
            .filter((r) => r.meta_status)
            .sort((a, b) => a.meta_priority - b.meta_priority)
        : [],
    [parsedData],
  );

  if (!parsedResult.success || !parsedData) {
    console.error("Comparison1004 validation failed", parsedResult.error);
    return null;
  }

  if (parsedData.status === false || parsedData.status === 0) return null;

  // Mirrors the CU organism's guard: nothing renders unless at least one row
  // has values for both columns.
  if (
    rows.length === 0 ||
    !rows.some((r) => r.meta_column1_text && r.meta_column2_text)
  ) {
    return null;
  }

  const backgroundColor = mounted && isMobile
    ? parsedData.mobile_background_color
    : parsedData.desktop_background_color;

  return (
    <section
      ref={sectionRef}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "mx-auto w-full flex items-center justify-center",
        "pt-[40px] pb-[60px] px-[12px]",
        "min-[1024px]:px-[48px] min-[1024px]:py-[40px]",
        "bg-[url('/images/comparison.webp')] bg-no-repeat",
      )}
      style={{ backgroundColor, fontFamily: "Poppins, sans-serif" }}
      aria-label={`Comparison between ${parsedData.column1} and ${parsedData.column2}`}
      data-widget="comparison-1004"
    >
      <div className="min-[1024px]:w-[1114px] flex flex-col items-center justify-center">
        <div className="flex items-center flex-col gap-[24px] min-[1024px]:flex-row min-[1024px]:gap-[12px]">
          {/* Left title block */}
          <div
            className={cn(
              "text-[28px] font-semibold leading-normal text-center flex flex-col justify-center gap-[14px] w-[260px]",
              "min-[1024px]:text-[32px] min-[1024px]:text-left min-[1024px]:items-start min-[1024px]:gap-[28px] min-[1024px]:w-[232px]",
            )}
            role="heading"
            aria-level={2}
          >
            {parsedData.pre_title ? (
              <p style={{ color: parsedData.pre_title_color }}>{parsedData.pre_title}</p>
            ) : null}
            {parsedData.title ? (
              <p style={{ color: parsedData.title_start_color }}>V/S</p>
            ) : null}
            <p style={{ color: parsedData.title_start_color }}>{parsedData.title}</p>

            {parsedData.description ? (
              <p
                className="text-[18px] leading-[1.5] font-normal mt-[8px] min-[1024px]:mt-[12px]"
                style={{ color: parsedData.description_text_color }}
              >
                {parsedData.description}
              </p>
            ) : null}
          </div>

          {/* Comparison grid: [category chip | column 1 | column 2] per row */}
          <div className="grid grid-cols-3">
            {rows.map((row, index) => {
              const isFirst = index === 0;
              const isLast = index === rows.length - 1;
              return (
                <Fragment key={row.id ?? index}>
                  {/* Category chip */}
                  <div
                    className={cn("p-0 min-[1024px]:px-[24px]", {
                      "pt-[66px] min-[1024px]:pt-[92px]": isFirst,
                      "pt-[8px] min-[1024px]:pt-[28px]": !isFirst,
                      "pb-[12px] min-[1024px]:pb-[14px]": isLast,
                    })}
                  >
                    <div
                      className={cn(
                        "flex flex-row items-center border",
                        "rounded-tl-[6px] rounded-bl-[6px] rounded-tr-[2px] rounded-br-[2px]",
                        "min-[1024px]:rounded-tl-[10px] min-[1024px]:rounded-bl-[10px] min-[1024px]:rounded-tr-[4px] min-[1024px]:rounded-br-[4px]",
                        "px-[6px] py-[8px] gap-[8px] min-[1024px]:px-[10px] min-[1024px]:gap-[14px]",
                      )}
                      style={{
                        backgroundColor: row.meta_criteria_background_color,
                        borderColor: row.meta_criteria_border_color,
                      }}
                    >
                      {row.meta_criteria_image ? (
                        <>
                          <Image
                            src={row.meta_criteria_image}
                            alt={row.meta_criteria_name || ""}
                            width={68}
                            height={56}
                            className="hidden min-[1024px]:block"
                          />
                          <Image
                            src={row.meta_criteria_image}
                            alt={row.meta_criteria_name || ""}
                            width={28}
                            height={28}
                            className="block min-[1024px]:hidden"
                          />
                        </>
                      ) : null}
                      <p className="text-[12px] font-semibold text-left min-[1024px]:text-[14px] min-[1024px]:leading-[1.5] min-[1024px]:font-normal">
                        {row.meta_criteria_name}
                      </p>
                    </div>
                  </div>

                  {/* Column 1 (CU Online) */}
                  <div
                    className={cn(
                      "flex flex-col gap-[10px] px-[6px]",
                      "min-[1024px]:gap-[20px] min-[1024px]:px-[24px]",
                      {
                        "pt-[8px] min-[1024px]:pt-[16px]": isFirst,
                        "pt-[8px] min-[1024px]:pt-[28px]": !isFirst,
                        "pb-[12px] min-[1024px]:pb-[14px]": isLast,
                      },
                    )}
                    style={{ backgroundColor: parsedData.column1_background_color }}
                  >
                    {isFirst ? (
                      <div className="flex items-center justify-center border-b border-[#FFFFFF] h-[48px] min-[1024px]:h-[56px] min-[1024px]:items-start">
                        <p
                          className="text-center text-[14px] font-semibold leading-normal min-[1024px]:text-[18px]"
                          style={{ color: parsedData.column1_text_color }}
                        >
                          {parsedData.column1}
                        </p>
                      </div>
                    ) : null}
                    <div className="flex-1 flex flex-row items-center justify-center min-[1024px]:justify-start min-[1024px]:gap-[18px]">
                      {row.meta_column1_image ? (
                        <Image
                          src={row.meta_column1_image}
                          alt={row.meta_column1_text || ""}
                          width={24}
                          height={24}
                          className="size-[24px]"
                        />
                      ) : null}
                      <p
                        className="hidden min-[1024px]:block text-[14px] leading-[1.5] font-normal text-left"
                        style={{ color: parsedData.column1_text_color }}
                      >
                        {row.meta_column1_text}
                      </p>
                    </div>
                  </div>

                  {/* Column 2 (Traditional Learning) */}
                  <div
                    className={cn(
                      "flex flex-col gap-[10px] px-[6px]",
                      "min-[1024px]:gap-[20px] min-[1024px]:px-[24px]",
                      {
                        "pt-[8px] min-[1024px]:pt-[16px]": isFirst,
                        "pt-[8px] min-[1024px]:pt-[28px]": !isFirst,
                        "pb-[12px] min-[1024px]:pb-[14px]": isLast,
                      },
                    )}
                    style={{ backgroundColor: parsedData.column2_background_color }}
                  >
                    {isFirst ? (
                      <div className="flex items-center justify-center border-b border-[#E6E6E6] h-[48px] min-[1024px]:h-[56px] min-[1024px]:items-start">
                        <p
                          className="text-center text-[14px] font-semibold leading-normal min-[1024px]:text-[18px]"
                          style={{ color: parsedData.column2_text_color }}
                        >
                          {parsedData.column2}
                        </p>
                      </div>
                    ) : null}
                    <div className="flex-1 flex flex-row items-center justify-center min-[1024px]:justify-start min-[1024px]:gap-[18px]">
                      {row.meta_column2_image ? (
                        <Image
                          src={row.meta_column2_image}
                          alt={row.meta_column2_text || ""}
                          width={24}
                          height={24}
                          className="size-[24px]"
                        />
                      ) : null}
                      <p
                        className="hidden min-[1024px]:block text-[14px] leading-[1.5] font-normal text-left"
                        style={{ color: parsedData.column2_text_color }}
                      >
                        {row.meta_column2_text}
                      </p>
                    </div>
                  </div>
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Comparison1004;
